import { MenuItem } from '@mui/material';
import { type Dayjs, dayjs } from 'date';
import { forwardRef, useState } from 'react';

import { type DateRange, DateRangePicker } from '../DateRangePicker/DateRangePicker';
import { Select, type SelectProps } from '../Select/Select';

export type SelectDatePickerProps = Omit<SelectProps, 'onChange' | 'value' | 'defaultValue'> & {
  onChange: ({ from, to }: DateRange) => void;
  options: { label: string; value: Dayjs }[];
  defaultValue: Dayjs | undefined;
};

const formatDateValue = (date: Dayjs | undefined) => (date ? date.toISOString() : undefined);

export const SelectDatePicker = forwardRef(function SelectDatePicker(
  { onChange, options, defaultValue, ...rest }: SelectDatePickerProps,
  ref
) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<Dayjs | undefined | 'range'>(defaultValue);
  const [rangeValue, setRangeValue] = useState<DateRange>({
    from: dayjs(defaultValue),
    to: dayjs(defaultValue).add(1, 'week'),
  });

  const selectValue = value === 'range' ? 'range' : formatDateValue(value);
  const defaultRangeValue =
    value === 'range'
      ? rangeValue
      : {
          from: dayjs(),
          to: dayjs().add(1, 'week'),
        };

  return (
    <Select
      data-testid="select-date-picker"
      value={value ? selectValue : ''}
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;

        const isClickOnSelect = target.getAttribute('role') === 'combobox';
        if (isClickOnSelect) {
          setOpen(true);
          return;
        }

        const isClickOutsideComponent = target.classList.contains('MuiBackdrop-root');
        if (isClickOutsideComponent) {
          setOpen(false);
        }
      }}
      onChange={(event) => {
        event.stopPropagation();
        if (!event.target.value || event.target.value === 'range') {
          return;
        }
        setValue(dayjs(event.target.value));

        const selectedDate = dayjs(event.target.value);
        const currentDate = dayjs();
        if (selectedDate.isBefore(currentDate)) {
          onChange({
            from: selectedDate,
            to: currentDate,
          });
          setOpen(false);
          return;
        }
        onChange({
          from: currentDate,
          to: selectedDate,
        });
        setOpen(false);
      }}
      renderValue={(value) => {
        if (value === 'range') {
          return `${rangeValue?.from.format('DD/MM/YYYY')} - ${rangeValue?.to.format('DD/MM/YYYY')}`;
        }
        const selectedOption = options.find((option) => option.value.isSame(value, 'date'));
        return selectedOption ? selectedOption.label : value.toString();
      }}
      defaultValue={formatDateValue(defaultValue)}
      {...rest}
      open={open}
      ref={ref}
    >
      <MenuItem value="range">
        <DateRangePicker
          onChange={({ from, to }) => {
            setValue('range');

            onChange({
              from,
              to,
            });

            setRangeValue({ from, to });
          }}
          onAccept={() => {
            setOpen(false);
          }}
          defaultValues={defaultRangeValue}
        />
      </MenuItem>

      {options.map(({ label, value }) => (
        <MenuItem key={label} value={value.toISOString()}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );
});
